import { isFeatureOn } from './appConfig';
import { foldAnswer, normalizeAnswer } from './normalizeAnswer';

/**
 * Сверка того, что ученик набрал, с самим словом.
 *
 * ⚠️ ЗАЧЕМ ОТДЕЛЬНАЯ ФУНКЦИЯ, А НЕ ПРОСТОЕ РАВЕНСТВО. Слово в справочнике не
 * всегда чистое: куратор вставляет списком, и в базу попадает «commitment
 * (noun)», «face-to-face (adj/adv)». Озвучка при этом произносит просто
 * «commitment» — ученик слышит слово, пишет его верно и получает «неправильно»,
 * а под ответом ему показывают «Correct: commitment (noun)». Догадаться, что от
 * него хотели дописать часть речи в скобках, невозможно (найдено заказчиком
 * 06.09.2026).
 *
 * Поэтому сверяем ЗНАЧИМУЮ часть:
 *   • без пояснения в скобках — это пометка для куратора, а не часть слова;
 *   • без разницы в регистре и лишних пробелах;
 *   • дефис и обычный пробел считаем равными: «face-to-face» и «face to face»
 *     на слух неразличимы, а придираться к чёрточке — не про язык.
 *
 * ⚠️ ЧТО МЫ НАМЕРЕННО НЕ ПРОЩАЕМ: опечатки в самих буквах. Упражнение «набрать
 * по буквам» ровно про написание — прощать буквы значит обессмыслить его.
 */
export function normalizeWordAnswer(s: string): string {
  // ⚠️ ОБЩАЯ ОСНОВА ИДЁТ ПЕРВОЙ и делится с сервером (lib/normalizeAnswer.ts ↔
  // shared/textnorm): начертание апострофов и кавычек, невидимые символы,
  // регистр, пробелы. Телефон ставит «’» вместо «'», и без этого шага ученик,
  // набравший слово верно, получал «неправильно» (09.09.2026).
  //
  // ⚠️ Порядок важен. Основа складывает «–» и «—» к «-», а правило ниже затем
  // превращает «-» в пробел — поблажка «face-to-face» = «face to face»
  // сохраняется. Поменяешь порядок — потеряешь её.
  return normalizeAnswer(s)
    // пояснение в скобках: «(noun)», «(adj/adv)»
    .replace(/\([^)]*\)/g, ' ')
    // дефисы и подчёркивания — как пробелы
    .replace(/[-–—_]/g, ' ')
    // хвостовая пунктуация
    .replace(/[.,!?;:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Больше стольких сочетаний не раскрываем — как `maxWordVariants` в Go. */
const MAX_WORD_VARIANTS = 32;

function wholeWord(s: string): string[] {
  const w = normalizeWordAnswer(s);
  return w ? [w] : [];
}

/**
 * Варианты слова через «/» — близнец `textnorm.WordVariants` на сервере
 * (договор vocab-alternatives.md, 11.09.2026; фидбек проверяющей 10.09, п. 7).
 *
 * ⚠️ ЗАЧЕМ. Слово «toast / speech» засчитывалось только полной строкой. Сервер
 * теперь принимает любой вариант, и без этой функции расходились экран и база:
 * ученик набрал «toast», в базе зачёт, а на экране красное поле.
 *
 *   • «/» с пробелом хотя бы с одной стороны — варианты ВСЕЙ фразы:
 *     «toast / speech» → speech, toast;
 *   • «/» без пробелов — варианты ОДНОГО слова, остальная фраза при нём:
 *     «get – got – got/gotten» → «get got got», «get got gotten». Так записаны
 *     формы глагола — 6 из 7 слов с «/» на стенде;
 *   • скобки снимаются ДО деления, иначе транскрипция «(/red/)» дала бы вариант;
 *   • запятая — НЕ разделитель (решение владельца 11.09.2026);
 *   • больше 32 сочетаний — не раскрываем, вариант один: `normalizeWordAnswer`.
 *
 * Порядок — отсортированный; сверка с Go идёт по общим векторам как множество
 * (`normalizeAnswerMatch.test.ts`).
 */
export function wordVariants(s: string): string[] {
  const base = normalizeAnswer(s).replace(/\([^)]*\)/g, ' ');
  const seen = new Set<string>();
  for (const phrase of base.split(/\s+\/\s*|\/\s+/)) {
    let combos = [''];
    const tokens = phrase.replace(/[-–—_.,!?;:]/g, ' ').split(/\s+/).filter(Boolean);
    for (const token of tokens) {
      const alts = token.split('/').filter(Boolean);
      if (alts.length === 0) continue;
      if (combos.length * alts.length > MAX_WORD_VARIANTS) return wholeWord(s);
      const next: string[] = [];
      for (const c of combos) for (const a of alts) next.push(`${c} ${a}`.trim());
      combos = next;
    }
    for (const c of combos) if (c !== '') seen.add(c);
  }
  if (seen.size > MAX_WORD_VARIANTS) return wholeWord(s);
  return [...seen].sort();
}

/** Есть ли у слова несколько вариантов — для подсказки «засчитается любой». */
export function hasWordVariants(word: string): boolean {
  return wordVariants(word).length > 1;
}

/**
 * Прежнее правило сверки слова — близнец `textnorm.EqualWord`.
 *
 * ⚠️ ПЕРВАЯ ВЕТКА — ПРЕЖНЕЕ РАВЕНСТВО, И ОНА ОБЯЗАТЕЛЬНА. Варианты только
 * расширяют правило: всё, что засчитывалось раньше, засчитывается и теперь
 * (пример, который без неё сузится, — «/» на ключ «/»). Вторая ветка: каждый
 * вариант набранного есть среди вариантов ключа — один вариант, оба, обратный
 * порядок; чужой вариант в наборе («toast / cat») — неверно.
 *
 * ⚠️ ТРЕТЬЯ ВЕТКА: «/» В ОТВЕТЕ ДЕЛИТСЯ ЦЕЛИКОМ (12.09.2026, договор §5).
 * Живой случай: ключ «In my opinion / In my view / To my mind», ученица набрала
 * всё три слитно через «/». Правило выше раскрывает такой ответ ПО СЛОВАМ —
 * слитная «/» означает вариант одного слова, — и получается «in my opinion my
 * view my mind»: ни одного осмысленного сочетания. Незачёт, а под ответом ей
 * показали ровно то же самое как верное.
 *
 * ⚠️ КЛЮЧ ПРИ ЭТОМ ЦЕЛИКОМ НЕ ДЕЛИТСЯ, и в этом вся разница с правилом уроков
 * (`slash_variants.go`), где делятся обе стороны. Здесь так нельзя: ключи вида
 * «get – got – got/gotten» — формы глагола, и разделить такой ключ значит
 * засчитать «gotten» за всю тройку. Отсюда же сторож «у ключа без „/“ ветка
 * молчит»: иначе «cat/dog» на ключ «cat» стало бы верным.
 *
 * Эталон правила — `services/vocab-service/internal/service/slash_whole.go`
 * (`wholeSlashMatch`); согласие держат общие векторы `word_equal_slash`.
 */
/**
 * Третья ветка отдельной функцией — близнец `wholeSlashMatch` в
 * `services/vocab-service/internal/service/slash_whole.go`.
 *
 * ⚠️ ВЫНЕСЕНА НЕ ДЛЯ КРАСОТЫ, А ЧТОБЫ ЕЁ МОЖНО БЫЛО ПРОВЕРИТЬ. Внутри
 * `equalWord` её поведение не видно вовсе: вторая ветка перехватывает те же
 * случаи раньше и отвечает «верно» сама. Намеренные поломки 12.09.2026 это и
 * показали — снятая проверка «у ключа нет „/“» и снятый выброс пустых частей не
 * уронили ни одного сценария, хотя правило было сломано. На сервере функция
 * отдельная по той же причине.
 */
export function wholeSlashMatch(typed: string, word: string): boolean {
  // Сторож взят из правила урока: иначе «cat/dog» на ключ «cat» стало бы верным.
  if (!word.includes('/')) return false;
  const keys = new Set(wordVariants(word));
  const parts = typed.split('/').map(normalizeWordAnswer).filter(Boolean);
  return parts.length > 0 && parts.every((p) => keys.has(p));
}

function equalWord(typed: string, word: string): boolean {
  const a = normalizeWordAnswer(typed);
  if (a !== '' && a === normalizeWordAnswer(word)) return true;
  const got = wordVariants(typed);
  const keys = new Set(wordVariants(word));
  if (got.length > 0 && got.every((g) => keys.has(g))) return true;
  return wholeSlashMatch(typed, word);
}

/**
 * Русские буквы, которые на экране не отличить от латинских, — близнец `cyrillicTwins`
 * в `services/vocab-service/internal/service/answer_fold.go`.
 *
 * ⚠️ ЗАПИСАНЫ КОДАМИ, А НЕ БУКВАМИ. Латинская «a» на месте русской выглядит так же, и
 * правка «на глаз» тихо сделала бы замену пустой. Строчные в к м н т не заменяются —
 * на латиницу похожи только заглавные (ключи с Caps Lock на русской раскладке).
 */
const TWIN_CODES: Array<[number, string]> = [
  [0x0430, 'a'], [0x0435, 'e'], [0x043e, 'o'], [0x0441, 'c'], [0x0443, 'y'], [0x0440, 'p'], [0x0445, 'x'],
  [0x0410, 'A'], [0x0415, 'E'], [0x041e, 'O'], [0x0421, 'C'], [0x0423, 'Y'], [0x0420, 'P'], [0x0425, 'X'],
  // казахская «і» и заглавные, похожие на латиницу только в верхнем регистре
  [0x0456, 'i'], [0x0406, 'I'], [0x0412, 'B'], [0x041a, 'K'], [0x041c, 'M'], [0x041d, 'H'], [0x0422, 'T'],
];
const TWINS = new Map(TWIN_CODES.map(([cp, lat]) => [String.fromCodePoint(cp), lat]));
const replaceTwins = (s: string) => Array.from(s, (ch) => TWINS.get(ch) ?? ch).join('');
const hasCyrillic = (s: string) => /\p{Script=Cyrillic}/u.test(s);

/** Где артикль НЕ снимается: «a few» — «несколько», «few» — «мало». */
const ARTICLE_KEPT = ['a few', 'a little', 'a lot', 'a bit', 'a couple'];
const LEADING_ARTICLES = ['a ', 'an ', 'the '];

/** Один артикль перед словом. Артикль без слова («the») остаётся как есть. */
function dropLeadingArticle(s: string): string {
  const f = foldAnswer(s);
  for (const p of ARTICLE_KEPT) if (f === p || f.startsWith(`${p} `)) return s;
  for (const a of LEADING_ARTICLES) {
    if (!f.startsWith(a)) continue;
    const rest = f.slice(a.length).trim();
    if (rest !== '') return rest;
  }
  return s;
}

/**
 * Складывает ответ и ключ для ВТОРОЙ, мягкой ветки сверки — близнец `foldEnglishAnswer`
 * (договор docs/CONTRACTS/vocab-answer-fold.md, просьба владельца 12.09.2026).
 *
 * Прощаются две вещи, и обе не про знание языка:
 *   • буквы-двойники — только когда ключ английский (после замены в нём не осталось
 *     кириллицы). В русском ключе «с» и «c» — разные буквы по делу;
 *   • один ведущий артикль a/an/the — с обеих сторон.
 *
 * ⚠️ ПОРЯДОК: сначала буквы, потом артикль. Иначе «thе apple» с русской «е» артиклем
 * не распознается.
 */
export function foldEnglishAnswer(typed: string, key: string): [string, string] {
  const latinKey = replaceTwins(key ?? '');
  if (!hasCyrillic(latinKey)) {
    typed = replaceTwins(typed ?? '');
    key = latinKey;
  }
  return [dropLeadingArticle(typed), dropLeadingArticle(key)];
}

/**
 * Совпал ли ответ ученика со словом.
 *
 * Прежнее правило (`equalWord`) **или** — при включённой мягкой сверке — то же правило на
 * сложенных строках (`foldEnglishAnswer`). Живой случай: эталон `strawberrу` с русской «у»,
 * 22 попытки, 0 засчитано.
 *
 * ⚠️ МЯГКАЯ ВЕТКА ЗА ФЛАГОМ `vocab_answer_fold` (умолчание — выключено). Сервер судит ответ
 * сам; пока на проде старый `vocab-service`, фронт с мягкой сверкой показал бы галочку при
 * ошибке в базе (договор §3 — бэкенд первым). `fold` явно — только для проверок.
 *
 * ⚠️ Первую ветку не убирать и не сливать со второй: «the-apple» на ключ «the apple»
 * засчитывается только ею (артикль вторая ветка снимет лишь у ключа).
 */
export function isWordAnswerCorrect(typed: string, word: string, fold = isFeatureOn('vocab_answer_fold')): boolean {
  if (equalWord(typed, word)) return true;
  if (!fold) return false;
  const [t, k] = foldEnglishAnswer(typed, word);
  return equalWord(t, k);
}
